/**
 * Lintico PT custom events.
 *
 * 当前版本：实现 size_change / color_change / collection_product_click / quick_view_click / nav_category_click / nav_level*_click。
 *
 * 字段规范（与 Custom Pixel 侧一致）：
 *   item_category_id   - product.type（与 name 同值，Shopify 的 type 没有数字 ID）
 *   item_category_name - product.type
 *   item_id            - product.id
 *   item_name          - product.title
 *   item_size          - 选中尺码值（option 名含 "size" 的那一项）
 *   original_price     - variant.compare_at_price / 100（单价，元，保留 2 位小数）
 *   price              - variant.price / 100（单价，元，保留 2 位小数）
 *   subtotal           - price × quantity（行小计，元）
 *   discount           - (original_price − price) × quantity（行让利，元；无划线价为 0）
 *   products           - [{ item_id, sku_id, item_name, price, original_price }]
 *   sku_id             - variant.id
 *   spu_id             - product.id
 *   variant            - variant.title
 *
 * 链路：theme JS -> Shopify.analytics.publish('lintico:size_change', payload)
 *      -> Custom Pixel(`Ptengine Tag`) -> ptengine.track('size_change', payload)
 */
(function () {
  const productJsonByVariantId = {};

  function rememberProductJson(product) {
    const variants = product && Array.isArray(product.variants) ? product.variants : [];
    variants.forEach(function (variant) {
      if (variant && variant.id != null) {
        productJsonByVariantId[String(variant.id)] = {
          product: product,
          variant: variant
        };
      }
    });
  }

  // ===== 全局 fetch 拦截器：往 /cart/add 请求里塞 _lintico_compare_at_price =====
  // 让划线价跟着 cart line 流到 checkout，Custom Pixel 在 lineItem.properties[] 里读出。
  // 数据源（按优先级）：
  //   1) 卡片 DOM 上的 [data-lintico-variant-id]+[data-lintico-compare-at-price-cents]（列表 / quick-buy）
  //   2) PDP product JSON（<form data-product>）反查当前选中 variant 的 compare_at_price
  //   3) 已请求过的 /products/*.js product JSON（gallery 自定义加购入口）
  // 找不到 / 划线价 ≤ 现价 → 不塞，让兜底回落策略生效。
  if (window.fetch && !window.__linticoFetchPatched) {
    window.__linticoFetchPatched = true;
    const origFetch = window.fetch;

    function lookupCompareAtCents(variantId) {
      if (!variantId) return null;
      const id = String(variantId);
      // 1) 列表卡片 / quick-buy 触发：DOM 上有静态 data-* 属性
      const cardEl = document.querySelector(
        '[data-lintico-variant-id="' + id + '"][data-lintico-compare-at-price-cents]'
      );
      if (cardEl) {
        const compareAtCents = parseInt(cardEl.getAttribute('data-lintico-compare-at-price-cents'), 10);
        const priceCents = parseInt(cardEl.getAttribute('data-lintico-price-cents'), 10);
        if (compareAtCents > 0 && Number.isFinite(priceCents) && compareAtCents > priceCents) {
          return compareAtCents;
        }
      }
      // 2) PDP form 上的 product JSON：选中 variant 切换后该 JSON 内变体仍是同一份
      const productForms = document.querySelectorAll('[data-product]');
      for (let i = 0; i < productForms.length; i++) {
        try {
          const productJson = JSON.parse(productForms[i].getAttribute('data-product') || '{}');
          const variant = (productJson.variants || []).find(function (v) {
            return String(v.id) === id;
          });
          if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
            return variant.compare_at_price;
          }
        } catch (e) { /* ignore */ }
      }
      const cached = productJsonByVariantId[id];
      if (cached && cached.variant && cached.variant.compare_at_price && cached.variant.compare_at_price > cached.variant.price) {
        return cached.variant.compare_at_price;
      }
      return null;
    }

    function publishCartAddAfter(responsePromise, items) {
      return responsePromise.then(function (response) {
        if (response && response.ok && Array.isArray(items)) {
          items.forEach(function (item) {
            const payload = buildAddCartPayload(item.id, item.quantity);
            if (payload) publish('product_added_to_cart', payload);
          });
        }
        return response;
      });
    }

    window.fetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/products\/[^/?#]+\.js(\?|$)/.test(url)) {
          return origFetch.apply(this, arguments).then(function (response) {
            if (!response || !response.ok || typeof response.clone !== 'function') return response;
            return response.clone().json().then(function (product) {
              rememberProductJson(product);
              return response;
            }).catch(function () {
              return response;
            });
          });
        }
        if (!/\/cart\/add(\.js)?(\?|$)/.test(url) || !init) {
          return origFetch.apply(this, arguments);
        }
        const linticoAddItems = [];
        let requestInit = init;
        const body = init.body;
        // FormData 路径（main.js PDP 主入口、表单 fetch 提交等）
        if (body instanceof FormData) {
          linticoAddItems.push({
            id: body.get('id'),
            quantity: parseInt(body.get('quantity') || '1', 10) || 1
          });
          if (!body.has('properties[_lintico_compare_at_price]')) {
            const cmp = lookupCompareAtCents(body.get('id'));
            if (cmp) body.append('properties[_lintico_compare_at_price]', String(cmp));
          }
        } else if (typeof body === 'string' && body.length > 0 && body.charAt(0) === '{') {
          // JSON 路径（gallery section / 自定义 fetch 加购）
          let parsed;
          try { parsed = JSON.parse(body); } catch (e) { parsed = null; }
          if (parsed) {
            const enrich = function (item) {
              if (!item || (item.properties && item.properties._lintico_compare_at_price)) return;
              const cmp = lookupCompareAtCents(item.id);
              if (!cmp) return;
              item.properties = Object.assign({}, item.properties || {}, {
                _lintico_compare_at_price: String(cmp)
              });
            };
            if (Array.isArray(parsed.items)) {
              parsed.items.forEach(function (item) {
                enrich(item);
                linticoAddItems.push({
                  id: item && item.id,
                  quantity: parseInt((item && item.quantity) || '1', 10) || 1
                });
              });
            } else if (parsed.id) {
              enrich(parsed);
              linticoAddItems.push({
                id: parsed.id,
                quantity: parseInt(parsed.quantity || '1', 10) || 1
              });
            }
            requestInit = Object.assign({}, init, { body: JSON.stringify(parsed) });
          }
        }
        return publishCartAddAfter(origFetch.call(this, input, requestInit), linticoAddItems);
      } catch (e) { /* silent fallback */ }
      return origFetch.apply(this, arguments);
    };
  }

  const PREFIX = 'lintico:';
  const DEBUG_KEY = 'linticoTrackingDebug';

  function isDebugEnabled() {
    try {
      return (
        window.location.search.includes('lintico_tracking_debug=1') ||
        window.localStorage.getItem(DEBUG_KEY) === '1'
      );
    } catch (error) {
      return false;
    }
  }

  function log() {
    if (isDebugEnabled()) {
      console.log.apply(console, ['[LinticoTracking]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function warn() {
    if (isDebugEnabled()) {
      console.warn.apply(console, ['[LinticoTracking]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function cleanString(value) {
    if (value === null || typeof value === 'undefined') return '';
    return String(value)
      .replace(/[;\n\r\t]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function cleanProductsString(value) {
    if (value === null || typeof value === 'undefined') return '';
    return String(value)
      .replace(/[\n\r\t]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function toScalar(value) {
    if (value === null || typeof value === 'undefined') return '';
    if (typeof value === 'number') return Number.isFinite(value) ? value : '';
    if (typeof value === 'string') return cleanString(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (Array.isArray(value)) return value.map(cleanString).filter(Boolean).join(';');
    if (typeof value === 'object') return Object.values(value).map(cleanString).filter(Boolean).join(';');
    return cleanString(value);
  }

  // GA4 client_id：_ga cookie 去掉 GA1.<n>. 前缀后，就是 BigQuery GA4 导出
  // 里的 user_pseudo_id（2026-08-21 实测：推导值在 BQ 中精确命中该访客的
  // 历史会话）。GA4 由 web pixel sandbox 内的第三方应用发送，无法往里注入
  // lt_uid，反向读取是唯一可行路径。
  //
  // 为什么 PT 侧也要带：cart attributes 那条路只覆盖加过购物车的人，而这里
  // 能让纯浏览、从不加购的访客同样建立 lt_uid ↔ user_pseudo_id 的边。
  //
  // ⚠️ 这里发送 _ga 的**原始值**（含 GA1.<n>. 前缀），而非去前缀的 client_id。
  // 去前缀后形如 "616075850.1787330123"，Ptengine 会将其识别为数字并按 double
  // 存储；该值有 19-20 位有效数字，超出 double 约 17 位的精度上限，尾部必然
  // 丢失 —— 2026-08-22 实测 CDP Export 返回 616075850.178733（float 类型），
  // 10 位时间戳被截成 6 位，值完全无法与 GA4 的 user_pseudo_id 匹配。
  // 保留 GA 前缀后首字符为字母，必被当作字符串，保真。
  //
  // 数仓侧统一用 REGEXP_REPLACE(v, r'^GA\d+\.\d+\.', '') 归一即可 —— 该表达式
  // 对 cart attributes 中已去前缀的值不匹配、原样返回，两处共用一套清洗逻辑。
  function readGaRaw() {
    var m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    return m ? m[1] : null;
  }

  function normalizePayload(payload) {
    const result = {};
    Object.keys(payload || {}).forEach(function (key) {
      result[key] = key === 'products' ? cleanProductsString(payload[key]) : toScalar(payload[key]);
    });
    return result;
  }

  function buildProductsString(item) {
    const data = item || {};
    return [
      'item_id=' + cleanString(data.item_id),
      'spu_id=' + cleanString(data.spu_id),
      'sku_id=' + cleanString(data.sku_id),
      'item_name=' + cleanString(data.item_name),
      'price=' + cleanString(data.price),
      'original_price=' + cleanString(data.original_price),
      'quantity=' + cleanString(data.quantity || 1)
    ].join(';');
  }

  function safeText(value) {
    if (value === null || typeof value === 'undefined') return null;
    const s = String(value).trim();
    return s.length ? s : null;
  }

  function centsToAmount(cents) {
    if (cents === null || typeof cents === 'undefined') return null;
    const n = Number(cents);
    if (!Number.isFinite(n)) return null;
    return Math.round(n) / 100;
  }

  function amountOrPrice(compareAtAmount, priceAmount) {
    if (
      compareAtAmount === null ||
      typeof compareAtAmount === 'undefined' ||
      compareAtAmount === 0 ||
      (priceAmount !== null && typeof priceAmount !== 'undefined' && compareAtAmount <= priceAmount)
    ) {
      return '';
    }
    return compareAtAmount;
  }

  function originalPriceFromCents(compareAtCents, priceCents) {
    return amountOrPrice(centsToAmount(compareAtCents), centsToAmount(priceCents));
  }

  function toNumber(value) {
    if (value === null || typeof value === 'undefined' || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function multiplyAmount(value, quantity) {
    const amount = toNumber(value);
    const qty = toNumber(quantity) || 1;
    if (amount === null) return '';
    return Math.round(amount * qty * 100) / 100;
  }

  function normalizeImageUrl(value) {
    if (!value) return null;
    if (typeof value === 'string') return safeText(value);
    if (value.src) return safeText(value.src);
    if (value.url) return safeText(value.url);
    if (value.preview_image) return normalizeImageUrl(value.preview_image);
    return null;
  }

  function readProductFromDom(formEl) {
    const root = (formEl && formEl.closest && formEl.closest('.js-product')) || document;
    const input = root.querySelector('#product-data, [data-product]');
    if (!input) return null;
    const raw = input.getAttribute('data-product');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      warn('failed to parse data-product json', error);
      return null;
    }
  }

  function findProductVariantById(variantId) {
    if (!variantId) return null;
    const id = String(variantId);
    const inputs = document.querySelectorAll('#product-data, [data-product]');
    for (let i = 0; i < inputs.length; i++) {
      const raw = inputs[i].getAttribute('data-product');
      if (!raw) continue;
      try {
        const product = JSON.parse(raw);
        const variant = (product.variants || []).find(function (v) {
          return String(v.id) === id;
        });
        if (variant) return { product: product, variant: variant };
      } catch (error) { /* ignore */ }
    }
    const cached = productJsonByVariantId[id];
    if (cached) return cached;
    return null;
  }

  function selectedVariantIdFromPage() {
    const href = window.location && window.location.href || '';
    const match = String(href).match(/[?&]variant=([^&#]+)/);
    if (match) return safeText(decodeURIComponent(match[1]));

    const input = document.querySelector('form[action*="/cart/add"] [name="id"], [name="id"]');
    if (!input) return null;
    return safeText(input.value || input.getAttribute('value'));
  }

  function getOptionName(option) {
    if (!option) return null;
    return safeText(option.name || option);
  }

  function optionNameMatches(optionName, tokens) {
    const normalized = String(optionName || '').toLowerCase();
    return tokens.some(function (token) {
      return normalized.indexOf(token) > -1;
    });
  }

  function isSizeOptionName(optionName) {
    return optionNameMatches(optionName, [
      'size',
      'taille',
      'größe',
      'grosse',
      'tamaño',
      'tamanho',
      'taglia',
      'maat',
      '尺码',
      '尺寸',
      'サイズ'
    ]);
  }

  function isColorOptionName(optionName) {
    return optionNameMatches(optionName, [
      'color',
      'colour',
      'couleur',
      'farbe',
      'colore',
      'kleur',
      'cor',
      '颜色',
      '顏色',
      '色',
      'カラー'
    ]);
  }

  function findOptionValue(product, variant, matcher) {
    if (!product || !variant) return null;
    const options = Array.isArray(product.options) ? product.options : [];
    for (let i = 0; i < options.length; i++) {
      const optName = getOptionName(options[i]);
      if (optName && matcher(optName)) {
        const key = 'option' + (i + 1);
        return variant[key] != null ? String(variant[key]) : null;
      }
    }
    return null;
  }

  function getCategory(product) {
    if (!product) return { id: null, name: null };
    const type = safeText(product.type);
    return { id: type, name: type };
  }

  function getVariantImage(product, variant) {
    return normalizeImageUrl(
      (variant && (variant.featured_image || variant.featured_media)) ||
      (product && (product.featured_image || product.featured_media || product.image))
    );
  }

  function buildVariantPayload(product, variant, overrides) {
    const category = getCategory(product);
    const item_id = product && product.id != null ? product.id : null;
    const item_name = product ? safeText(product.title) : null;
    const sku_id = variant && variant.id != null ? variant.id : null;
    const price = centsToAmount(variant && variant.price);
    const original_price = amountOrPrice(centsToAmount(variant && variant.compare_at_price), price);
    const payload = Object.assign({
      currency: activeCurrency(),
      index: 1,
      item_category_id: category.id,
      item_category_name: category.name,
      item_id: item_id,
      item_name: item_name,
      item_size: safeText(findOptionValue(product, variant, isSizeOptionName)),
      item_color: safeText(findOptionValue(product, variant, isColorOptionName)),
      original_price: original_price,
      price: price,
      image_url: getVariantImage(product, variant),
      sku_id: sku_id,
      sku: variant ? safeText(variant.sku) : null,
      spu_id: item_id,
      vendor: product ? safeText(product.vendor) : null,
      variant: variant ? safeText(variant.title) : null
    }, overrides || {});

    applyLineTotals(payload);

    payload.products = buildProductsString({
      item_id: payload.item_id,
      spu_id: payload.spu_id,
      sku_id: payload.sku_id,
      item_name: payload.item_name,
      price: payload.price,
      original_price: payload.original_price,
      quantity: 1
    });

    return payload;
  }

  function datasetValue(el, names) {
    for (let i = 0; i < names.length; i++) {
      const name = names[i];
      if (el.dataset && Object.prototype.hasOwnProperty.call(el.dataset, name)) {
        return safeText(el.dataset[name]);
      }
      const attrName = name.replace(/[A-Z]/g, function (letter) {
        return '-' + letter.toLowerCase();
      });
      const attrValue = el.getAttribute && el.getAttribute('data-' + attrName);
      if (safeText(attrValue)) return safeText(attrValue);
    }
    return null;
  }

  function getPageType() {
    const bodyClass = document.body ? document.body.className : '';
    if (bodyClass.indexOf('template-product') > -1) return 'product';
    if (bodyClass.indexOf('template-collection') > -1) return 'collection';
    if (bodyClass.indexOf('template-search') > -1) return 'search';
    if (bodyClass.indexOf('template-index') > -1) return 'home';
    const path = window.location.pathname || '';
    if (path.indexOf('/products/') > -1) return 'product';
    if (path.indexOf('/collections/') > -1) return 'collection';
    if (path.indexOf('/search') > -1) return 'search';
    return 'other';
  }

  function getLocationType(el, fallback) {
    const explicit = el.closest && el.closest('[data-lintico-location]');
    if (explicit) return datasetValue(explicit, ['linticoLocation']) || fallback;
    if (el.closest && el.closest('.section-featured-collection')) return 'featured_collection';
    if (el.closest && el.closest('.section-product-grid')) return 'product_list';
    if (el.closest && el.closest('.section-main-collection')) return 'collection_grid';
    if (getPageType() === 'collection') return 'collection_grid';
    return fallback;
  }

  // index 语义统一为「商品在当前页列表中的位置序号」（从 1 开始，仅统计当前已渲染的当页商品）。
  // 纯运行时按 DOM 顺序计算，不读 Liquid 传入的 data-lintico-index，这样语义在所有列表页一致，
  // 且 section 编辑（增删/是否传 index 参数）都不会破坏该序号，无需后续编辑者手动对齐。
  function getCardIndex(el) {
    const card = el.closest && el.closest('[data-lintico-product-card]');
    if (!card) return null;
    const cards = Array.prototype.slice.call(document.querySelectorAll('[data-lintico-product-card]'));
    const idx = cards.indexOf(card);
    return idx > -1 ? idx + 1 : null;
  }

  function buildCardPayload(el, overrides) {
    const item_id = datasetValue(el, ['linticoProductId', 'productId', 'itemId']);
    const sku_id = datasetValue(el, ['linticoVariantId', 'variantId', 'gridItemVariant']);
    const item_name = datasetValue(el, ['linticoProductTitle', 'productTitle', 'itemName']);
    const category = datasetValue(el, ['linticoProductType', 'productType', 'itemCategoryName']);
    const variantTitle = datasetValue(el, ['linticoVariantTitle']);
    const price = centsToAmount(datasetValue(el, ['linticoPriceCents']));
    const original_price = amountOrPrice(
      centsToAmount(datasetValue(el, ['linticoCompareAtPriceCents'])),
      price
    );
    const base = {
      currency: activeCurrency(),
      image_url: datasetValue(el, ['linticoImageUrl']),
      index: getCardIndex(el),
      item_category_id: category,
      item_category_name: category,
      item_color: parseColorFromTitle(variantTitle),
      item_id: item_id,
      item_name: item_name,
      item_size: parseSizeFromTitle(variantTitle),
      location_locationtype: getLocationType(el, 'collection_grid'),
      original_price: original_price,
      page_type: getPageType(),
      price: price,
      sku: datasetValue(el, ['linticoSku']),
      sku_id: sku_id,
      spu_id: item_id,
      vendor: datasetValue(el, ['linticoVendor']),
      variant: variantTitle
    };

    // 若用户在该卡片上切过色（color_change），用记住的选中变体覆盖默认首个变体
    const cardEl = (el.closest && el.closest('[data-lintico-product-card]')) || el;
    const selected = cardEl && cardEl.__linticoSelectedVariant;
    if (selected) {
      Object.keys(selected).forEach(function (key) {
        const value = selected[key];
        if (value !== null && value !== undefined && value !== '') base[key] = value;
      });
    }

    const payload = Object.assign(base, overrides || {});

    applyLineTotals(payload);

    payload.products = buildProductsString({
      item_id: payload.item_id,
      spu_id: payload.spu_id,
      sku_id: payload.sku_id,
      item_name: payload.item_name,
      price: payload.price,
      original_price: payload.original_price,
      quantity: payload.quantity || 1
    });

    return payload;
  }

  function publishProductView() {
    if (getPageType() !== 'product') return;
    const variantId = selectedVariantIdFromPage();
    const match = variantId ? findProductVariantById(variantId) : null;
    const product = (match && match.product) || readProductFromDom(document);
    const variant = (match && match.variant) || (product && product.variants && product.variants[0]);
    if (!product || !variant) return;
    publish('product_view', buildVariantPayload(product, variant, {
      page_type: 'product',
      quantity: 1
    }));
  }

  function variantIdFromHref(href) {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.href);
      return safeText(url.searchParams.get('variant'));
    } catch (e) {
      const match = String(href).match(/[?&]variant=([^&#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    }
  }

  function findVariantTrackingElement(root, variantId) {
    if (!root || !variantId || !root.querySelectorAll) return null;
    const candidates = root.querySelectorAll('[data-lintico-variant-id]');
    for (let i = 0; i < candidates.length; i += 1) {
      if (datasetValue(candidates[i], ['linticoVariantId']) === String(variantId)) return candidates[i];
    }
    return null;
  }

  function applyVariantTrackingData(payload, variantEl) {
    if (!payload || !variantEl) return payload;
    const variantId = datasetValue(variantEl, ['linticoVariantId']);
    const price = centsToAmount(datasetValue(variantEl, ['linticoPriceCents']));
    const originalPrice = amountOrPrice(
      centsToAmount(datasetValue(variantEl, ['linticoCompareAtPriceCents'])),
      price
    );
    const imageUrl = datasetValue(variantEl, ['linticoImageUrl']);

    if (variantId) payload.sku_id = variantId;
    if (price !== null && price !== '') payload.price = price;
    payload.original_price = originalPrice;
    if (imageUrl) payload.image_url = imageUrl;
    applyLineTotals(payload);
    payload.products = buildProductsString(payload);
    return payload;
  }

  // 定位某 variant 所在的集合卡片，用于给加购事件回填真实网格位置
  function findCardElForVariant(variantId) {
    if (!variantId) return null;
    const id = String(variantId);
    const direct = document.querySelector('[data-lintico-product-card][data-lintico-variant-id="' + id + '"]');
    if (direct) return direct;
    const anyEl = document.querySelector('[data-lintico-variant-id="' + id + '"]');
    const viaClosest = anyEl && anyEl.closest && anyEl.closest('[data-lintico-product-card]');
    if (viaClosest) return viaClosest;
    // 切色后加购：卡片默认 variant id 不等于所加 variant，靠记住的选中变体匹配
    const cards = document.querySelectorAll('[data-lintico-product-card]');
    for (let i = 0; i < cards.length; i++) {
      const sel = cards[i].__linticoSelectedVariant;
      if (sel && String(sel.sku_id) === id) return cards[i];
    }
    return null;
  }

  function buildAddCartPayload(variantId, quantity) {
    const qty = toNumber(quantity) || 1;
    const match = findProductVariantById(variantId);
    if (match) {
      // price/original_price 保持单价；subtotal/discount 已按 quantity 在 buildVariantPayload 内算好
      const payload = buildVariantPayload(match.product, match.variant, { quantity: qty });
      // 集合卡片加购：用卡片真实网格位置覆盖 PDP 默认的 index=1（详情页主商品无卡片，保持 1）
      const cardForIndex = findCardElForVariant(variantId);
      if (cardForIndex) {
        const cardIndex = getCardIndex(cardForIndex);
        if (cardIndex !== null && cardIndex !== '') payload.index = cardIndex;
      }
      payload.products = buildProductsString(payload);
      return payload;
    }

    const cardEl = variantId
      ? document.querySelector('[data-lintico-variant-id="' + String(variantId) + '"][data-lintico-price-cents]')
      : null;
    if (cardEl) {
      const payload = buildCardPayload(cardEl, { quantity: qty });
      payload.products = buildProductsString(payload);
      return payload;
    }

    return null;
  }

  function publish(eventShortName, payload) {
    const fullEventName = eventShortName.indexOf(PREFIX) === 0 ? eventShortName : PREFIX + eventShortName;
    const data = normalizePayload(Object.assign({}, payload || {}, {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      source: 'shopify_theme',
      published_at: new Date().toISOString(),
      // lt_uid：first-party UID，由 uid-bootstrap 异步签发（受 consent 门控）。
      // 带上它，PT 侧的每条事件都能与 GA4 / Shopify 订单串到同一个人身上。
      //
      // PT 事件层的 user_identifier 覆盖 100% 且跨会话稳定（2026-08-21 实测），
      // 因此只要该访客有任意一条事件带上 lt_uid，其全部 PT 事件（含 SDK 自动
      // 采集的 stay / click / page_view）都能经 user_identifier 关联到同一人。
      //
      // 取不到时不伪造 —— 经 toScalar 后落为空字符串，数仓侧需
      // NULLIF(TRIM(lt_uid), '') 归一为 NULL，否则空串会把大量无关访客错连成
      // 同一个 person。这是整条链路上最容易出事的一处。
      lt_uid: window.LT_UID || null,
      // GA4 关联边（原始 _ga 值，见 readGaRaw 注释）；同样需要空串归一
      lt_ga: readGaRaw()
    }));

    try {
      window.__linticoPublishedEvents = window.__linticoPublishedEvents || [];
      window.__linticoPublishedEvents.push({ name: fullEventName, data: data });

      if (!window.Shopify || !window.Shopify.analytics ||
          typeof window.Shopify.analytics.publish !== 'function') {
        warn('Shopify.analytics.publish unavailable', fullEventName, data);
        return false;
      }
      window.Shopify.analytics.publish(fullEventName, data);
      log('published', fullEventName, data);
      return true;
    } catch (error) {
      console.error('[LinticoTracking] publish failed', fullEventName, error);
      return false;
    }
  }

  function trackPtengineDirect(eventShortName, payload) {
    const eventName = eventShortName.indexOf(PREFIX) === 0
      ? eventShortName.slice(PREFIX.length)
      : eventShortName;
    const data = normalizePayload(Object.assign({}, payload || {}, {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      source: 'shopify_theme_direct',
      published_at: new Date().toISOString(),
      lt_uid: window.LT_UID || null,
      lt_ga: readGaRaw()
    }));

    try {
      window.__linticoPtengineDirectEvents = window.__linticoPtengineDirectEvents || [];
      window.__linticoPtengineDirectEvents.push({ name: eventName, data: data });

      if (!window.ptengine || typeof window.ptengine.track !== 'function') {
        warn('ptengine.track unavailable', eventName, data);
        return false;
      }
      window.ptengine.track(eventName, data, { transport: 'beacon' });
      log('ptengine direct track', eventName, data);
      return true;
    } catch (error) {
      console.error('[LinticoTracking] ptengine direct track failed', eventName, error);
      return false;
    }
  }

  function bindVariantChange() {
    document.addEventListener('on:variant:change', function (event) {
      const detail = event.detail || {};
      const variant = detail.variant;
      if (!variant) return;

      const changedOptionName = detail.changedOptionName || null;
      const changedOptionValue = detail.changedOptionValue || null;

      const product = readProductFromDom(detail.form || event.target);
      if (!product) {
        warn('product data not found, skipping variant change tracking');
        return;
      }

      if (changedOptionName && isSizeOptionName(changedOptionName)) {
        publish('size_change', buildVariantPayload(product, variant, {
          item_size: safeText(changedOptionValue) || safeText(findOptionValue(product, variant, isSizeOptionName))
        }));
      }

      if (changedOptionName && isColorOptionName(changedOptionName)) {
        publish('color_change', buildVariantPayload(product, variant, {
          item_color: safeText(changedOptionValue) || safeText(findOptionValue(product, variant, isColorOptionName))
        }));
      }
    });
  }

  function bindCollectionProductClick() {
    document.addEventListener('click', function (event) {
      const link = event.target.closest && event.target.closest('[data-lintico-product-link], product-block a.product-link, product-grid-item-variant a[data-grid-link]');
      if (!link) return;
      if (event.target.closest && event.target.closest('[data-lintico-quick-view-trigger], [data-lintico-quick-view]')) return;

      const href = link.href || link.getAttribute('href') || '';
      const source = link.closest('[data-lintico-product-card]') || link;
      const cardRoot = link.closest('product-block') || source;
      const payload = buildCardPayload(source, {
        href: href,
        page_type: getPageType(),
        location_locationtype: getLocationType(source, 'collection_grid')
      });
      const variantEl = findVariantTrackingElement(cardRoot, variantIdFromHref(href));

      publish('collection_product_click', applyVariantTrackingData(payload, variantEl));
    });
  }

  function bindQuickViewClick() {
    document.addEventListener('click', function (event) {
      const trigger = event.target.closest && event.target.closest('[data-lintico-quick-view-trigger], [data-lintico-quick-view], .quickbuy-toggle, .global-quickbuy-toggle, product-grid-item-variant .quickview button, product-grid-item-variant [aria-haspopup="true"]');
      if (!trigger) return;

      const source = trigger.closest('[data-lintico-product-card]') || trigger;

      publish('quick_view_click', buildCardPayload(source, {
        href: trigger.href || trigger.getAttribute('href') || '',
        page_type: getPageType(),
        location_locationtype: 'quick_view'
      }));
    });
  }

  function normalizePathFromUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (e) {
      return String(url).split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
    }
  }

  function destinationTypeFromUrl(url, path) {
    if (!url && !path) return 'other';
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin) return 'external';
    } catch (e) { /* ignore */ }
    if (/^\/collections(\/|$)/.test(path || '')) return 'collection';
    if (/^\/products(\/|$)/.test(path || '')) return 'product';
    if (/^\/pages(\/|$)/.test(path || '')) return 'page';
    if (/^\/blogs(\/|$)/.test(path || '')) return 'blog';
    if (/^\/search(\/|$)/.test(path || '')) return 'search';
    if (path === '/') return 'home';
    return 'other';
  }

  function linkText(link) {
    if (!link) return '';
    const aria = link.getAttribute('aria-label');
    const text = link.textContent;
    return cleanString(aria || text || '');
  }

  function directChildLink(item) {
    if (!item) return null;
    for (let i = 0; i < item.children.length; i++) {
      const child = item.children[i];
      if (child && child.matches && child.matches('a.navigation__link, a.lintico-moment-nav__heading, a.lintico-lookbook-nav__heading')) {
        return child;
      }
    }
    return null;
  }

  function navDepth(link) {
    const tier3 = link.closest('.navigation__tier-3, .lintico-lookbook-nav__list');
    if (tier3) return 3;
    const tier2 = link.closest('.navigation__tier-2, .lintico-nav-cards, .lintico-moment-nav__body, .lintico-mega__features, .lintico-mobile-stories');
    if (tier2) return 2;
    return 1;
  }

  function topLevelNavLink(link) {
    const topItem = link.closest('.navigation__tier-1 > .navigation__item');
    return directChildLink(topItem);
  }

  function parentColumnLink(link) {
    const depth = navDepth(link);
    if (depth < 3) return null;
    const columnItem = link.closest('.navigation__tier-2 .navigation__item--with-children, .lintico-lookbook-nav');
    if (!columnItem) return null;
    return directChildLink(columnItem) || columnItem.querySelector('a.lintico-lookbook-nav__heading');
  }

  function navEntryType(link) {
    if (link.closest('.lintico-feature-card, .lintico-nav-card, .lintico-mobile-story, .lintico-moment-card')) return 'image_card';
    if (link.closest('.menu-promotion')) return 'promo';
    if (/button-row__btn|small-feature-link|lintico-moment-card__cta|lintico-feature-card__cta/i.test(link.className || '')) return 'cta';
    return 'text';
  }

  function navContext(link) {
    if (link.closest('.mobile-navigation-drawer')) return 'mobile_drawer';
    if (link.closest('.navigation--left, .navigation--main, .header-navigation, .site-header')) return 'desktop_header';
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
  }

  function buildNavCategoryPayload(link) {
    if (!link) return null;
    const href = link.href || link.getAttribute('href') || '';
    const destinationPath = normalizePathFromUrl(href);
    if (!destinationPath || destinationPath === '#') return null;

    const depth = navDepth(link);
    const topLink = topLevelNavLink(link);
    const parentLink = parentColumnLink(link);
    const label = linkText(link);
    const level1Label = topLink ? linkText(topLink) : (depth === 1 ? label : '');
    const level2Label = depth === 2 ? label : (parentLink ? linkText(parentLink) : '');
    const level3Label = depth === 3 ? label : '';

    return {
      event_name: 'nav_category_click',
      nav_version: 'native-nav-v1',
      device_type: window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop',
      nav_context: navContext(link),
      entry_type: navEntryType(link),
      nav_depth: depth,
      label: label,
      level1_label: level1Label,
      level2_label: level2Label,
      level3_label: level3Label,
      href: href,
      destination_path: destinationPath,
      destination_type: destinationTypeFromUrl(href, destinationPath),
      source_page: window.location.pathname,
      current_path: window.location.pathname,
      is_collection: destinationTypeFromUrl(href, destinationPath) === 'collection'
    };
  }

  function bindNavCategoryClick() {
    document.addEventListener('click', function (event) {
      const link = event.target.closest && event.target.closest(
        'main-navigation a.navigation__link, main-navigation a.lintico-feature-card, main-navigation a.lintico-nav-card__link, main-navigation a.lintico-mobile-story, main-navigation a.lintico-moment-card, main-navigation a.lintico-moment-nav__heading, main-navigation a.lintico-lookbook-nav__heading, main-navigation a.lintico-lookbook-nav__link, main-navigation a.menu-promotion__link, #proxy-nav a.navigation__link, #proxy-nav a.lintico-feature-card, #proxy-nav a.lintico-nav-card__link, #proxy-nav a.lintico-moment-card, #proxy-nav a.lintico-moment-nav__heading, #proxy-nav a.lintico-lookbook-nav__heading, #proxy-nav a.lintico-lookbook-nav__link, #proxy-nav a.menu-promotion__link, .mobile-navigation-drawer a.navigation__link, .mobile-navigation-drawer a.lintico-feature-card, .mobile-navigation-drawer a.lintico-nav-card__link, .mobile-navigation-drawer a.lintico-mobile-story, .mobile-navigation-drawer a.lintico-moment-card, .mobile-navigation-drawer a.lintico-moment-nav__heading, .mobile-navigation-drawer a.lintico-lookbook-nav__heading, .mobile-navigation-drawer a.lintico-lookbook-nav__link, .mobile-navigation-drawer a.menu-promotion__link'
      );
      if (!link) return;
      if (link.matches('.navigation__children-toggle') || link.getAttribute('href') === '#') return;

      const payload = buildNavCategoryPayload(link);
      if (!payload) return;
      trackPtengineDirect('nav_category_click', payload);
      trackPtengineDirect('nav_level' + payload.nav_depth + '_click', payload);
    }, true);
  }

  function parseSizeFromTitle(title) {
    if (!title || typeof title !== 'string') return null;
    const sizeRe = /^(XXXL|XXL|XL|L|M|S|XS|XXS|\d{1,3}|One\s*Size|Free\s*Size)$/i;
    const segments = title.split('/');
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i].trim();
      if (sizeRe.test(seg)) return seg;
    }
    return null;
  }

  // 从变体标题里取颜色段：取第一个「不是尺码」的段（覆盖 "Red / M"、单选项 "Red" 等常见情况）
  function parseColorFromTitle(title) {
    if (!title || typeof title !== 'string') return null;
    const segments = title.split('/');
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i].trim();
      if (seg && seg.toLowerCase() !== 'default title' && !parseSizeFromTitle(seg)) return seg;
    }
    return null;
  }

  function activeCurrency() {
    try {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    } catch (e) {
      return '';
    }
  }

  // Model A：price/original_price 为单价；补 subtotal=单价×数量、discount=(original−price)×数量
  function applyLineTotals(payload) {
    if (!payload) return payload;
    const qty = toNumber(payload.quantity) || 1;
    const unitPrice = toNumber(payload.price);
    const unitOriginal = toNumber(payload.original_price);
    payload.subtotal = unitPrice !== null ? Math.round(unitPrice * qty * 100) / 100 : '';
    payload.discount = (unitOriginal !== null && unitPrice !== null && unitOriginal > unitPrice)
      ? Math.round((unitOriginal - unitPrice) * qty * 100) / 100
      : 0;
    return payload;
  }

  function buildCartItemPayload(cartItemEl, overrides) {
    if (!cartItemEl) return null;
    const price = centsToAmount(datasetValue(cartItemEl, ['linticoPriceCents']));
    const original_price = amountOrPrice(
      centsToAmount(datasetValue(cartItemEl, ['linticoCompareAtPriceCents'])),
      price
    );
    const quantity = toNumber(datasetValue(cartItemEl, ['linticoQuantity']));
    const variantTitle = datasetValue(cartItemEl, ['linticoVariantTitle']) || null;
    const item = {
      currency: activeCurrency(),
      item_id: datasetValue(cartItemEl, ['linticoProductId']) || null,
      spu_id: datasetValue(cartItemEl, ['linticoProductId']) || null,
      sku_id: datasetValue(cartItemEl, ['linticoVariantId']) || null,
      sku: datasetValue(cartItemEl, ['linticoSku']) || null,
      item_name: datasetValue(cartItemEl, ['linticoProductTitle']) || null,
      item_color: parseColorFromTitle(variantTitle),
      item_size: parseSizeFromTitle(variantTitle),
      variant: variantTitle,
      price: price,
      original_price: original_price,
      image_url: normalizeImageUrl(datasetValue(cartItemEl, ['linticoImageUrl'])) || null,
      item_category_id: datasetValue(cartItemEl, ['linticoProductType']) || null,
      item_category_name: datasetValue(cartItemEl, ['linticoProductType']) || null,
      vendor: datasetValue(cartItemEl, ['linticoVendor']) || null,
      quantity: quantity
    };
    const payload = Object.assign({}, item, overrides || {}, {
      page_type: getPageType()
    });
    applyLineTotals(payload);
    payload.products = buildProductsString(payload);
    return payload;
  }

  function bindCartItemRemove() {
    document.addEventListener('click', function (event) {
      const trigger = event.target.closest && event.target.closest('.cart-item__remove');
      if (!trigger) return;
      const cartItemEl = trigger.closest('.cart-item');
      if (!cartItemEl) return;

      const payload = buildCartItemPayload(cartItemEl, {
        quantity_after: 0,
        quantity_removed: toNumber(datasetValue(cartItemEl, ['linticoQuantity']))
      });
      if (!payload) return;
      publish('product_removed_from_cart', payload);
    }, true);
  }

  function bindUserIdentify() {
    const userId = window.linticoCustomerId;
    if (!userId) {
      try { sessionStorage.removeItem('lintico_user_identify_reported'); } catch (e) {}
      return;
    }
    try {
      if (sessionStorage.getItem('lintico_user_identify_reported') === '1') return;
      sessionStorage.setItem('lintico_user_identify_reported', '1');
    } catch (e) {}
    publish('user_identify', {
      user_id: userId,
      email: window.shopifyEmail || ''
    });
  }

  // Shopify 没有官方登录事件，登录只发生在主题侧 /account/login 表单。
  // 提交时打标记，登录成功重定向后若「已登录 + 有标记」即发一次真实登录事件。
  function bindLoginTracking() {
    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (form && form.matches && form.matches('form[action*="/account/login"]')) {
        try { sessionStorage.setItem('lintico_pending_login', '1'); } catch (e) {}
      }
    }, true);

    try {
      if (window.linticoCustomerId && sessionStorage.getItem('lintico_pending_login') === '1') {
        sessionStorage.removeItem('lintico_pending_login');
        publish('user_login', {
          user_id: window.linticoCustomerId,
          email: window.shopifyEmail || ''
        });
      }
    } catch (e) {}
  }

  function bindCardColorSwatch() {
    document.addEventListener('click', function (event) {
      const swatch = event.target.closest && event.target.closest('.product-block-options__item');
      if (!swatch) return;
      const optionsWrap = swatch.closest('.product-block-options');
      if (!optionsWrap) return;
      const optionName = String(optionsWrap.getAttribute('data-option-name') || '').trim();
      if (!isColorOptionName(optionName)) return;

      const card = swatch.closest('product-block') || swatch.closest('[data-lintico-product-card]');
      if (!card) return;
      const link = card.closest('[data-lintico-product-card]') || card.querySelector('[data-lintico-product-card]') || card.querySelector('[data-lintico-product-id]') || card;

      const optionValue = swatch.getAttribute('data-option-item')
        || (swatch.querySelector('.product-block-options__item__text')
            ? swatch.querySelector('.product-block-options__item__text').textContent.trim()
            : null);

      const payload = buildCardPayload(link, {
        location_locationtype: 'collection_card_swatch',
        item_color: optionValue || null,
        option_name: optionName || null
      });
      if (!payload) return;

      const swatchVariantId = datasetValue(swatch, ['linticoVariantId']);
      const swatchSku = datasetValue(swatch, ['linticoSku']);
      const swatchVariantTitle = datasetValue(swatch, ['linticoVariantTitle']);
      const swatchPrice = centsToAmount(datasetValue(swatch, ['linticoPriceCents']));
      const swatchOriginalPrice = amountOrPrice(
        centsToAmount(datasetValue(swatch, ['linticoCompareAtPriceCents'])),
        swatchPrice
      );
      const swatchImageUrl = datasetValue(swatch, ['linticoImageUrl']);

      if (swatchVariantId) payload.sku_id = swatchVariantId;
      if (swatchSku) payload.sku = swatchSku;
      if (swatchVariantTitle) {
        payload.variant = swatchVariantTitle;
        const swatchSize = parseSizeFromTitle(swatchVariantTitle);
        if (swatchSize) payload.item_size = swatchSize;
      }
      if (swatchPrice !== null && swatchPrice !== '') payload.price = swatchPrice;
      payload.original_price = swatchOriginalPrice;
      if (swatchImageUrl) payload.image_url = swatchImageUrl;

      applyLineTotals(payload);
      payload.products = buildProductsString(payload);

      rememberSelectedVariant(link, payload);
      publish('color_change', payload);
    }, true);
  }

  // 把卡片上刚选中的变体记到卡片元素上，供之后的 quick_view_click / collection_product_click 复用。
  function rememberSelectedVariant(el, payload) {
    if (!el || !payload) return;
    const cardEl = (el.matches && el.matches('[data-lintico-product-card]'))
      ? el
      : (el.closest && el.closest('[data-lintico-product-card]'));
    if (!cardEl) return;
    cardEl.__linticoSelectedVariant = {
      sku_id: payload.sku_id,
      sku: payload.sku,
      variant: payload.variant,
      item_color: payload.item_color,
      item_size: payload.item_size,
      price: payload.price,
      original_price: payload.original_price,
      image_url: payload.image_url
    };
  }

  // globo.swatch.custom（第三方 swatch app）渲染的 .star-set-image 不在主题原生 DOM 树上，
  // 没有 data-* 暴露 variant id / option name，只能从 background-image URL 解颜色名。
  // URL 形如 https://cdn.starapps.studio/v2/apps/vsk/<shop>/swatches/<OptionName>/<size>/<ColorName>.webp
  // ColorName 段是双重 URL 编码（"Navy Blue" → "Navy%2520Blue"），需 decode 两次。
  function parseGloboSwatchUrl(bgImage) {
    if (!bgImage) return null;
    const m = String(bgImage).match(/\/swatches\/([^/]+)\/\d+\/([^/?"')]+)\.[a-z0-9]+/i);
    if (!m) return null;
    function safeDecode(s) {
      try { return decodeURIComponent(s); } catch (e) { return s; }
    }
    return {
      optionName: safeDecode(m[1]),
      optionValue: safeDecode(safeDecode(m[2]))
    };
  }

  function bindGloboCardSwatch() {
    document.addEventListener('click', function (event) {
      const swatch = event.target.closest && event.target.closest('.star-set-image');
      if (!swatch) return;

      const productBlock = swatch.closest('product-block');
      if (!productBlock) return;
      const link = productBlock.querySelector('[data-lintico-product-card]')
        || productBlock.querySelector('[data-lintico-product-id]')
        || productBlock;

      const parsed = parseGloboSwatchUrl(swatch.style.backgroundImage);
      const optionName = parsed && parsed.optionName;
      const optionValue = parsed && parsed.optionValue;

      // globo 也支持非颜色 swatch（如材质），按 option name 过滤；解不出来时按 color 处理（globo 列表常用场景）
      if (optionName && !isColorOptionName(optionName)) return;

      const payload = buildCardPayload(link, {
        location_locationtype: 'collection_card_swatch',
        item_color: optionValue || null,
        option_name: optionName || null
      });
      if (!payload) return;

      // globo swatch 没有 variant id/sku，只能记住颜色（之后 quick_view 反映颜色，sku 仍为默认变体）
      const cardEl = (link.matches && link.matches('[data-lintico-product-card]'))
        ? link
        : (link.closest && link.closest('[data-lintico-product-card]'));
      if (cardEl) cardEl.__linticoSelectedVariant = { item_color: optionValue || null };

      publish('color_change', payload);
    }, true);
  }

  window.LinticoTracking = window.LinticoTracking || {};
  window.LinticoTracking.publish = publish;
  window.LinticoTracking.buildProductsString = buildProductsString;
  window.LinticoTracking.normalizePayload = normalizePayload;
  window.LinticoTracking._test = {
    cleanString: cleanString,
    toScalar: toScalar,
    normalizePayload: normalizePayload,
    buildProductsString: buildProductsString,
    centsToAmount: centsToAmount,
    multiplyAmount: multiplyAmount,
    originalPriceFromCents: originalPriceFromCents,
    selectedVariantIdFromPage: selectedVariantIdFromPage,
    buildVariantPayload: buildVariantPayload,
    variantIdFromHref: variantIdFromHref,
    applyVariantTrackingData: applyVariantTrackingData,
    buildNavCategoryPayload: buildNavCategoryPayload
  };

  publishProductView();
  bindNavCategoryClick();
  bindVariantChange();
  bindCollectionProductClick();
  bindQuickViewClick();
  bindCartItemRemove();
  bindUserIdentify();
  bindLoginTracking();
  bindCardColorSwatch();
  bindGloboCardSwatch();
  log('initialized');
})();
